import { Box, Slider, styled, Typography, type BoxProps } from '@mui/material'
import { shortUuid } from '@tunasong/models'
import type { FC, MouseEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { usePixelsPerSecond } from '../hooks/pixels-per-second.js'
import { plugins as allPlugins, loopPlugins } from './plugins.js'
import type { TimelineEvent } from './timeline-event.js'
import { getClickPositionSeconds } from './util.js'

export interface TimelineProps extends Omit<BoxProps, 'onClick' | 'position'> {
  className?: string

  duration: number
  // Pixels per second for the timeline, used to calculate the width of the timeline
  // If not provided, it will be calculated based on the container width
  pixelsPerSecond?: number
  height?: number
  events?: TimelineEvent[]

  loopOnly?: boolean

  playbackRate?: number
  onPlaybackRateChange?(playbackRate: number): void

  /** Click on the timeline at position in seconds */
  onClick?(ev: MouseEvent, seconds: number, lane?: number): void
  onEventClick?(ev: MouseEvent, event: TimelineEvent): void
  onUpdateEvent?(event: TimelineEvent): void
}

interface TimelineContainerProps {
  containerHeight: number
}

const TimelineSvg = styled('svg')<TimelineContainerProps>(({ containerHeight }) => ({
  width: '100%',
  height: containerHeight,
}))

export const Timeline: FC<TimelineProps> = props => {
  const {
    className,
    duration,
    playbackRate = 1.0,
    onPlaybackRateChange,
    pixelsPerSecond: providedPps,
    onUpdateEvent,
    height = 36,
    events,
    onClick,
    onEventClick,
    loopOnly = false,
    ...boxProps
  } = props

  // Height is passed directly to styled components

  const [canvas, setCanvas] = useState<SVGElement | null>(null)
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null)

  let lanes = loopOnly ? 1 : 4
  const plugins = loopOnly ? loopPlugins : allPlugins

  const laneHeight = Math.round(height / lanes)

  const containerPixelsPerSecond = usePixelsPerSecond(rootEl, duration)

  const pixelsPerSecond = providedPps ?? containerPixelsPerSecond

  const timeInterval = useMemo(() => {
    /** Max 1 interval per 50 pixels, rounding to 10 second intervals */
    const pixelsPerInterval = 50
    const roundTo = 10
    const interval = (duration * pixelsPerSecond) / pixelsPerInterval
    const timeInterval = Math.floor(duration / interval)
    const val = Math.max(Math.ceil(timeInterval / roundTo) * roundTo, 1)
    return Number.isSafeInteger(val) ? val : 10
  }, [duration, pixelsPerSecond])

  const getLane = useCallback(
    (ev: MouseEvent) => {
      const top = canvas?.getBoundingClientRect().top ?? 0
      const y = ev.clientY - top
      const lane = Math.floor(y / laneHeight) + 1.0
      return lane
    },
    [canvas, laneHeight]
  )

  const isDraggingRef = useRef(false)

  const timeline = useMemo(() => {
    if (!canvas || !Number.isFinite(pixelsPerSecond) || pixelsPerSecond <= 0) {
      return []
    }
    /**
     * Layout is as follows:
     *
     * 1/4 is used for time labels
     * 1/4 is used for notches
     * 1/2 is used for labels (if labels are specified)
     */

    const positionEvents: TimelineEvent[] = []
    let seconds = 0
    for (let index = 0; index < duration / timeInterval; index++) {
      positionEvents.push({ id: shortUuid(), type: 'position', start: seconds, name: 'Notch' })
      seconds += timeInterval
    }

    const allEvents = [...positionEvents, ...(events ?? [])]

    const noopFn = () => {}

    /** Process the positions with the plugins */
    const elements = allEvents
      .map((event, eventIdx) =>
        plugins.map((Plugin, pluginIdx) => (
          <Plugin
            key={`${event.id ?? eventIdx}-${event.type}-plugin-${pluginIdx}`}
            event={event}
            laneHeight={laneHeight}
            onDragStart={() => (isDraggingRef.current = true)}
            onDragEnd={() => (isDraggingRef.current = false)}
            pixelsPerSecond={pixelsPerSecond}
            onEventClick={onEventClick ?? noopFn}
            onUpdateEvent={onUpdateEvent ?? noopFn}
          />
        ))
      )
      .flat()
    return elements
  }, [canvas, pixelsPerSecond, events, duration, timeInterval, plugins, laneHeight, onEventClick, onUpdateEvent])

  const handleClick = (ev: MouseEvent) => {
    if (!onClick || isDraggingRef.current) {
      return
    }

    const seconds = getClickPositionSeconds(ev, { duration, pixelsPerSecond })
    const lane = getLane(ev)
    onClick(ev, seconds, lane)
  }

  const [showSlider, setShowSlider] = useState(false)

  if (duration <= 0 && events && events?.length > 0) {
    return (
      <Typography
        data-testid="timeline"
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'center', userSelect: 'none' }}
      >
        Start the audio to see the timeline ({events.length} events)
      </Typography>
    )
  }

  return (
    <Box
      className={className}
      data-testid="timeline"
      sx={{
        width: '100%',
        position: 'relative',
        height,
        cursor: isDraggingRef.current ? 'grabbing' : 'crosshair',
      }}
      ref={setRootEl}
      draggable={false}
      {...boxProps}
    >
      {/* Playback rate control as a small dropdown on the absolute right */}
      {onPlaybackRateChange && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            minWidth: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Show only the label unless interacting */}
          <Box
            sx={{
              cursor: 'pointer',
              color: 'secondary.main',
              px: 1,
              userSelect: 'none',
              bgcolor: 'background.paper',
              fontSize: 12,
              fontWeight: 500,
              '&:hover': { bgcolor: 'action.hover', color: 'secondary.light' },
            }}
            onClick={e => {
              e.stopPropagation()
              setShowSlider(true)
            }}
            tabIndex={0}
          >
            {`${playbackRate}x`}
          </Box>
          {showSlider && (
            <Box
              sx={{
                ml: 1,
                backgroundColor: 'background.paper',
                opacity: 0.9,
                borderRadius: 1,
                p: 1,
                minWidth: 120,
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseLeave={() => setShowSlider(false)}
            >
              <Slider
                value={playbackRate}
                min={0.5}
                max={2}
                step={0.05}
                marks={[{ value: 0.5 }, { value: 0.75 }, { value: 1 }, { value: 1.25 }, { value: 1.5 }, { value: 2 }]}
                onChange={(_, value) => onPlaybackRateChange?.(typeof value === 'number' ? value : value[0])}
                valueLabelDisplay="auto"
                sx={{ width: 100, mx: 1 }}
                size="small"
                autoFocus
              />
            </Box>
          )}
        </Box>
      )}

      <TimelineSvg ref={setCanvas} containerHeight={height} preserveAspectRatio="none" onClick={handleClick}>
        {timeline}
      </TimelineSvg>
    </Box>
  )
}
